import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
    DEFAULT_AUCTION_TIMER,
    DEFAULT_BID_TIMER,
    DEFAULT_BUDGET,
    DEFAULT_NUM_PARTICIPANTS,
    DEFAULT_PLAYERS_PER_TEAM,
    MODES,
    ROLES,
    ROLE_LABELS,
} from '../domain/constants';
import { createAuction, joinAuction, spectateAuction } from '../lib/api';
import {
    loadSession,
    loadSessionByName,
    loadSpectatorSession,
    saveSession,
    saveSpectatorSession,
} from '../lib/session';

// Creazione e ingresso, un solo componente per entrambe le modalita.
// Prima erano quattro file quasi identici (CreateAuction, CreateRepairAuction,
// JoinAuction, JoinRepairAuction) che differivano per tre stringhe e un URL.

/** Input numerico che non produce mai NaN: prima un campo svuotato mandava
 *  `parseInt('')` al server e la creazione falliva in silenzio. */
function NumberField({ id, label, value, onChange, min, max }) {
    return (
        <div className="field">
            <label className="field-label" htmlFor={id}>{label}</label>
            <input
                id={id}
                type="number"
                min={min}
                max={max}
                className="input"
                value={value}
                onChange={(event) => onChange(event.target.value)}
            />
        </div>
    );
}

export default function AuctionForm({ action, mode }) {
    const navigate = useNavigate();
    const isCreate = action === 'create';
    // Lo schermo da proiettare non e un partecipante: non ha un nome, non
    // occupa un posto e non sceglie la modalita dell'asta. Gli servono solo
    // il nome della lobby e la password.
    const isSpectate = action === 'spectate';

    const [lobbyName, setLobbyName] = useState('');
    const [participantName, setParticipantName] = useState('');
    const [password, setPassword] = useState('');
    const [budget, setBudget] = useState(String(DEFAULT_BUDGET[mode]));
    const [numParticipants, setNumParticipants] = useState(String(DEFAULT_NUM_PARTICIPANTS));
    const [auctionTimer, setAuctionTimer] = useState(String(DEFAULT_AUCTION_TIMER));
    const [bidTimer, setBidTimer] = useState(String(DEFAULT_BID_TIMER));
    const [slots, setSlots] = useState({ ...DEFAULT_PLAYERS_PER_TEAM });
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    const submit = async (event) => {
        event.preventDefault();
        setError(null);
        setBusy(true);
        try {
            if (isSpectate) {
                const watched = await spectateAuction({
                    lobbyName: lobbyName.trim(),
                    password,
                    // Se questo computer guardava gia questa lobby, il suo
                    // token viene riconfermato invece di consumarne un altro.
                    token: loadSpectatorSession(lobbyName.trim())?.token,
                });
                saveSpectatorSession(watched.lobby_name, { token: watched.token });
                navigate(`/asta/${encodeURIComponent(watched.lobby_name)}/schermo`);
                return;
            }

            const response = isCreate
                ? await createAuction({
                      lobbyName: lobbyName.trim(),
                      mode,
                      participantName: participantName.trim(),
                      settings: {
                          budget: Number.parseInt(budget, 10),
                          num_participants: Number.parseInt(numParticipants, 10),
                          players_per_team: slots,
                          password,
                          auction_timer_x: Number.parseInt(auctionTimer, 10),
                          bid_timer_y: Number.parseInt(bidTimer, 10),
                      },
                  })
                : await joinAuction({
                      lobbyName: lobbyName.trim(),
                      password,
                      participantName: participantName.trim(),
                      // Chi si era gia collegato a questa lobby da qui rientra
                      // nella propria squadra invece di scontrarsi con il
                      // proprio stesso nome. Il token si cerca per nome:
                      // dallo stesso browser possono essere entrati in piu di
                      // uno, e ognuno deve riprendersi il proprio.
                      token: loadSessionByName(
                          lobbyName.trim(),
                          participantName.trim(),
                      )?.token,
                  });

            saveSession(response.lobby_name, {
                token: response.token,
                participantId: response.participant_id,
                // Il nome buono e quello che il server conferma: un rientro
                // puo averlo lasciato com'era.
                name: response.participant_name ?? participantName.trim(),
                mode: response.mode,
            });
            navigate(`/asta/${encodeURIComponent(response.lobby_name)}`);
        } catch (apiError) {
            // Prima un nome di lobby gia in uso non produceva alcun messaggio.
            setError(apiError.message);
        } finally {
            setBusy(false);
        }
    };

    const numbersValid = [budget, numParticipants, auctionTimer, bidTimer].every((value) =>
        Number.isInteger(Number.parseInt(value, 10)),
    );
    const canSubmit =
        lobbyName.trim()
        && (isSpectate || participantName.trim())
        && (!isCreate || numbersValid)
        && !busy;

    return (
        <form className="stack" onSubmit={submit}>
            <h2>
                {isCreate
                    ? 'Crea una nuova asta'
                    : isSpectate
                      ? "Guarda un'asta"
                      : "Entra in un'asta"}
            </h2>

            {isSpectate && (
                <p className="u-dim">
                    Schermata da proiettare su un monitor, una TV o un proiettore: mostra
                    l&apos;asta a caratteri grandi in sola lettura. Non occupa un posto fra
                    i partecipanti e non puo&apos; fare offerte.
                </p>
            )}

            <div className="field">
                <label className="field-label" htmlFor="lobby">Nome dell&apos;asta:</label>
                <input
                    id="lobby"
                    type="text"
                    className="input"
                    value={lobbyName}
                    onChange={(event) => setLobbyName(event.target.value)}
                    // Chi torna in un'asta a cui era gia collegato da qui si
                    // ritrova il proprio nome: e quello che il server
                    // riconoscera insieme al token.
                    onBlur={() => {
                        if (isCreate || participantName) return;
                        const known = loadSession(lobbyName.trim());
                        if (known?.name) setParticipantName(known.name);
                    }}
                    placeholder="Es. Asta Amici"
                />
            </div>

            {!isSpectate && (
                <div className="field">
                    <label className="field-label" htmlFor="participant">Il tuo nome (o della tua squadra):</label>
                    <input
                        id="participant"
                        type="text"
                        className="input"
                        value={participantName}
                        onChange={(event) => setParticipantName(event.target.value)}
                        placeholder="Es. Real Madrid"
                    />
                </div>
            )}

            <div className="field">
                <label className="field-label" htmlFor="password">Password{isCreate ? ' (facoltativa)' : ''}:</label>
                <input
                    id="password"
                    type="password"
                    className="input"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder={isCreate ? 'Lascia vuoto se non serve' : "Password dell'asta"}
                />
            </div>

            {isCreate && (
                <>
                    <NumberField id="budget" label="Budget:" value={budget} onChange={setBudget} min={1} />
                    <NumberField
                        id="participants"
                        label="Numero di partecipanti:"
                        value={numParticipants}
                        onChange={setNumParticipants}
                        min={2}
                        max={20}
                    />
                    <NumberField
                        id="auctionTimer"
                        label="Durata dell'asta su un giocatore (secondi):"
                        value={auctionTimer}
                        onChange={setAuctionTimer}
                        min={3}
                        max={300}
                    />
                    <NumberField
                        id="bidTimer"
                        label="Tempo rimesso a ogni rilancio (secondi):"
                        value={bidTimer}
                        onChange={setBidTimer}
                        min={1}
                        max={300}
                    />
                    <fieldset className="field">
                        <legend className="field-label">Giocatori per ruolo</legend>
                        <div className="field-row">
                            {ROLES.map((role) => (
                                <label key={role} className="field" htmlFor={`slot-${role}`}>
                                    <span className="field-label">{ROLE_LABELS[role]}</span>
                                    <input
                                        id={`slot-${role}`}
                                        type="number"
                                        min="0"
                                        max="30"
                                        className="input"
                                        value={slots[role]}
                                        onChange={(event) =>
                                            setSlots((current) => ({
                                                ...current,
                                                [role]: Number.parseInt(event.target.value, 10) || 0,
                                            }))
                                        }
                                    />
                                </label>
                            ))}
                        </div>
                    </fieldset>
                </>
            )}

            {error && <div className="notice notice--stop">{error}</div>}

            <button type="submit" className="btn btn--primary" disabled={!canSubmit}>
                {busy ? 'Attendi…' : isCreate ? 'Crea' : isSpectate ? 'Guarda' : 'Entra'}
            </button>
            {mode === MODES.REPAIR && isCreate && (
                <p className="u-dim">
                    Nell&apos;asta di riparazione potrai importare le rose della stagione in corso
                    e svincolare i giocatori prima di ogni fase.
                </p>
            )}
        </form>
    );
}
